/**
 * Budget Allocation page: submit a budget and preferences, then view the
 * engine's diversified, explainable recommendation.
 */

import React, { useState } from "react";
import { apiClient } from "@/api/client";
import type { AxiosError } from "axios";
import type {
  AllocationRecommendation,
  AllocationRequestPayload,
  InvestmentHorizon,
  RiskTolerance,
} from "@/types";
import DataTable, { DataTableColumn } from "@/components/tables/DataTable";
import type { AllocationPositionRecord } from "@/types";

const ROLE_LABEL: Record<string, string> = {
  core_holding: "Core holding",
  growth_satellite: "Growth satellite",
  defensive_holding: "Defensive",
  income_holding: "Income",
};

const columns: DataTableColumn<AllocationPositionRecord>[] = [
  { header: "Symbol", accessor: (row) => <span className="font-mono">{row.symbol}</span> },
  {
    header: "Role",
    accessor: (row) => (row.portfolio_role ? ROLE_LABEL[row.portfolio_role] ?? row.portfolio_role : "—"),
  },
  { header: "Allocation", accessor: (row) => `$${Number(row.dollar_amount).toLocaleString()}`, align: "right" },
  { header: "Weight", accessor: (row) => `${row.weight_pct}%`, align: "right" },
  { header: "Score", accessor: (row) => row.research_score_at_time?.toFixed(0) ?? "—", align: "right" },
];

export default function BudgetAllocation(): React.ReactElement {
  const [totalBudget, setTotalBudget] = useState("10000");
  const [cashReserve, setCashReserve] = useState("5");
  const [riskTolerance, setRiskTolerance] = useState<RiskTolerance>("moderate");
  const [horizon, setHorizon] = useState<InvestmentHorizon>("long_term");
  const [maxPosition, setMaxPosition] = useState("20");
  const [symbolsInput, setSymbolsInput] = useState("");
  const [excludedInput, setExcludedInput] = useState("");
  const [preferredSectorsInput, setPreferredSectorsInput] = useState("");

  const [recommendation, setRecommendation] = useState<AllocationRecommendation | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setRecommendation(null);
    try {
      const payload: AllocationRequestPayload = {
        total_budget: totalBudget,
        cash_reserve_pct: cashReserve,
        risk_tolerance: riskTolerance,
        investment_horizon: horizon,
        max_position_size_pct: maxPosition,
        preferred_sectors: preferredSectorsInput
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        excluded_industries: excludedInput
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        candidate_symbols: symbolsInput
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      };
      const { data: request } = await apiClient.post("/allocations/requests", payload);
      const { data: rec } = await apiClient.post<AllocationRecommendation>(
        `/allocations/requests/${request.id}/generate`
      );
      setRecommendation(rec);
    } catch (err) {
      const axiosError = err as AxiosError<{ detail: string }>;
      setError(axiosError.response?.data?.detail ?? "Unable to generate an allocation.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Budget allocation</h1>
        <p className="mt-1 text-sm text-mist-400">
          Turn a budget and your preferences into a diversified, explainable allocation across
          researched companies.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="panel grid grid-cols-1 gap-4 p-6 sm:grid-cols-2">
        <div>
          <label className="field-label">Total budget ($)</label>
          <input
            value={totalBudget}
            onChange={(e) => setTotalBudget(e.target.value)}
            className="field-input"
            inputMode="decimal"
          />
        </div>
        <div>
          <label className="field-label">Cash reserve (%)</label>
          <input
            value={cashReserve}
            onChange={(e) => setCashReserve(e.target.value)}
            className="field-input"
            inputMode="decimal"
          />
        </div>
        <div>
          <label className="field-label">Risk tolerance</label>
          <select
            value={riskTolerance}
            onChange={(e) => setRiskTolerance(e.target.value as RiskTolerance)}
            className="field-input"
          >
            <option value="conservative">Conservative</option>
            <option value="moderate">Moderate</option>
            <option value="aggressive">Aggressive</option>
          </select>
        </div>
        <div>
          <label className="field-label">Investment horizon</label>
          <select
            value={horizon}
            onChange={(e) => setHorizon(e.target.value as InvestmentHorizon)}
            className="field-input"
          >
            <option value="short_term">Short term</option>
            <option value="medium_term">Medium term</option>
            <option value="long_term">Long term</option>
          </select>
        </div>
        <div>
          <label className="field-label">Max position size (%)</label>
          <input
            value={maxPosition}
            onChange={(e) => setMaxPosition(e.target.value)}
            className="field-input"
            inputMode="decimal"
          />
        </div>
        <div>
          <label className="field-label">Preferred sectors (comma-separated, optional)</label>
          <input
            value={preferredSectorsInput}
            onChange={(e) => setPreferredSectorsInput(e.target.value)}
            className="field-input"
            placeholder="Healthcare, Technology"
          />
        </div>
        <div>
          <label className="field-label">Excluded industries (comma-separated)</label>
          <input
            value={excludedInput}
            onChange={(e) => setExcludedInput(e.target.value)}
            className="field-input"
            placeholder="Tobacco, Gambling"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">Candidate symbols (comma-separated, must already have research)</label>
          <input
            required
            value={symbolsInput}
            onChange={(e) => setSymbolsInput(e.target.value)}
            className="field-input"
            placeholder="AAPL, MSFT, JNJ"
          />
        </div>
        <div className="sm:col-span-2">
          <button type="submit" disabled={isSubmitting} className="btn-primary w-full sm:w-auto">
            {isSubmitting ? "Generating…" : "Generate allocation"}
          </button>
        </div>
      </form>

      {error && <p className="text-sm text-signal-rose">{error}</p>}

      {recommendation && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="panel p-5">
              <p className="text-xs uppercase tracking-wide text-mist-400">Total budget</p>
              <p className="mt-1 font-mono text-xl text-mist-100">
                ${Number(recommendation.total_budget).toLocaleString()}
              </p>
            </div>
            <div className="panel p-5">
              <p className="text-xs uppercase tracking-wide text-mist-400">Invested</p>
              <p className="mt-1 font-mono text-xl text-signal-teal">
                ${Number(recommendation.invested_amount).toLocaleString()}
              </p>
            </div>
            <div className="panel p-5">
              <p className="text-xs uppercase tracking-wide text-mist-400">Cash reserve</p>
              <p className="mt-1 font-mono text-xl text-mist-100">
                ${Number(recommendation.cash_reserve_amount).toLocaleString()}
              </p>
            </div>
          </div>

          <DataTable<AllocationPositionRecord>
            columns={columns}
            rows={recommendation.positions}
            rowKey={(row) => row.id}
            emptyMessage="No positions were allocated — budget remained in cash."
          />

          {recommendation.positions.length > 0 && (
            <div className="panel space-y-4 p-5">
              <h2 className="text-sm font-semibold text-mist-100">Reasoning</h2>
              {recommendation.positions.map((position) => (
                <div key={position.id} className="space-y-1 border-b border-ink-800 pb-3 last:border-0">
                  <p className="text-sm text-mist-300">
                    <span className="font-mono text-signal-teal">{position.symbol}</span> —{" "}
                    {position.reasoning}
                  </p>
                  {position.risk_explanation && (
                    <p className="text-xs text-mist-400">{position.risk_explanation}</p>
                  )}
                  {position.alignment_explanation && (
                    <p className="text-xs text-mist-400">{position.alignment_explanation}</p>
                  )}
                  {position.confidence !== null && (
                    <p className="text-xs text-mist-400">
                      Research confidence: {(Number(position.confidence) * 100).toFixed(0)}%
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          <p className="text-xs italic text-mist-400">{recommendation.disclaimer}</p>
        </div>
      )}
    </div>
  );
}
